import { cn } from "@/lib/utils";
import * as Toast from "@radix-ui/react-toast";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import React, { createContext, useContext, useState, JSX } from "react";

interface ShowToastOptions {
  title: string;
  description?: string;
  type?: "SUCCESS" | "ERROR" | "INFO" | "WARNING";
}

type ToastContextType = {
  showToastMultiple: (options: ShowToastOptions) => void;
};

interface ToastItem extends ShowToastOptions {
  id: number;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_ICON: Record<string, JSX.Element> = {
  SUCCESS: <CircleCheck size={16} />,
  ERROR: <CircleX size={16} />,
  INFO: <Info size={16} />,
  WARNING: <TriangleAlert size={16} />,
};

export const ToastProviderMultiple = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToastMultiple = ({ title, description, type = "SUCCESS" }: ShowToastOptions) => {
    const id = Date.now();

    setToasts((prev) => {
      // Remove any existing identical toast (same message/type)
      const filtered = prev.filter(
        (t) => !(t.title === title && t.description === description && t.type === type)
      );

      return [...filtered, { id, title, description, type }];
    });

    // Remove after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  };

  return (
    <ToastContext.Provider value={{ showToastMultiple }}>
      <Toast.Provider swipeDirection="right">
        {children}

        {toasts.map((toast) => (
          <Toast.Root
            key={toast.id}
            defaultOpen
            className={cn(
              "px-4 py-3 mb-3 rounded-lg shadow-md border animate-slide-in",
              toast.type === "SUCCESS" && "bg-green-400 text-green-900 border-green-700",
              toast.type === "ERROR" && "bg-red-400 text-red-900 border-red-700",
              toast.type === "INFO" && "bg-sky-400 text-sky-900 border-sky-700",
              toast.type === "WARNING" && "bg-orange-400 text-orange-900 border-orange-700"
            )}
          >
            <Toast.Title className="font-semibold flex items-center gap-1">
              {TOAST_ICON[toast.type]} {toast.title}
            </Toast.Title>
            {toast.description && (
              <Toast.Description className="text-sm opacity-80">
                {toast.description}
              </Toast.Description>
            )}
          </Toast.Root>
        ))}

        <Toast.Viewport className="fixed top-4 right-4 z-50 w-96 max-w-full outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
};

export const useToastMultiple = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToastMultiple must be used within a ToastProviderMultiple");
  return context;
};
